import { useCallback, useState } from "react";
import { loadPortfolioReportData } from "./loadReportData";
import type { PortfolioReportOptions } from "./types";

export type GenStatus = "idle" | "loading" | "rendering" | "done" | "error";

async function loadLogoAsDataUrl(): Promise<string | undefined> {
  try {
    const res = await fetch("/quantum-small.png");
    if (!res.ok) return undefined;
    const blob = await res.blob();
    return new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result as string);
      r.onerror = () => resolve(undefined);
      r.readAsDataURL(blob);
    });
  } catch {
    return undefined;
  }
}

function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function usePortfolioReportGenerator() {
  const [status, setStatus] = useState<GenStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(
    async (token: string, options: PortfolioReportOptions) => {
      let phase = "loading portfolio data";
      try {
        setStatus("loading");
        setError(null);
        const data = await loadPortfolioReportData(token, options);
        phase = "rendering report";
        const logoDataUrl = await loadLogoAsDataUrl();

        // Two maps when the spatial section is on:
        //   - US: choropleth of state-level PM2.5 intake share
        //   - EU: project markers on the EU/UK basemap
        let usHeatmapImage: string | undefined;
        let euMarkersImage: string | undefined;
        if (options.sections.spatial_map) {
          try {
            const { renderUsHeatmapImage, renderEuMarkersImage } =
              await import("./pdf/spatialImages");
            [usHeatmapImage, euMarkersImage] = await Promise.all([
              renderUsHeatmapImage(data.usStatesFC),
              renderEuMarkersImage(data.projects),
            ]);
          } catch (e) {
            console.warn("Portfolio spatial render failed, skipping:", e);
          }
        }
        // Legacy single-map var kept here only because the PDF document still
        // accepts it (PDF layout unchanged for now). Pass US heatmap to PDF.
        const mapImage = usHeatmapImage;

        // Chart images for the PPTX split slides (generation + the three
        // impact-category plots). PDF stays table/number-driven.
        const charts: {
          genChart?: string;
          healthChart?: string;
          bioChart?: string;
          econChart?: string;
        } = {};
        if (options.outputs.pptx) {
          try {
            // All four slides capture the same Chart.js charts the dashboard
            // cards use (shared builders) at upsized presentation fonts —
            // same look, correct cross-section economic data, readable in
            // the deck.
            const { captureImpactChart, captureGenerationChart } =
              await import("./pptx/captureImpactChart");
            if (options.sections.public_health)
              charts.healthChart = captureImpactChart(
                data.aggregate.impacts,
                "dalys",
              );
            if (options.sections.biodiversity)
              charts.bioChart = captureImpactChart(
                data.aggregate.impacts,
                "pdfs",
              );
            if (options.sections.economic)
              charts.econChart = captureImpactChart(
                data.aggregate.impacts,
                "economic",
              );
            if (options.sections.fuel_displacement)
              charts.genChart = captureGenerationChart(
                data.aggregate.generation,
              );
          } catch (e) {
            console.warn("Chart render failed, slides degrade:", e);
          }
        }

        setStatus("rendering");

        const safe = options.branding.title.replace(/[^a-zA-Z0-9_-]/g, "_");

        if (options.outputs.pdf) {
          const { pdf } = await import("@react-pdf/renderer");
          const { PortfolioReportDocument } = await import(
            "./pdf/PortfolioReportDocument"
          );
          const blob = await pdf(
            PortfolioReportDocument({ data, logoDataUrl, mapImage }),
          ).toBlob();
          download(blob, `${safe}_Portfolio_Report.pdf`);
        }

        if (options.outputs.pptx) {
          const { generatePortfolioPptx } = await import("./pptx/generatePptx");
          await generatePortfolioPptx(data, `${safe}_Portfolio_Report.pptx`, {
            mapImage,
            usHeatmapImage,
            euMarkersImage,
            ...charts,
          });
        }

        setStatus("done");
        setTimeout(() => setStatus("idle"), 2500);
      } catch (err) {
        console.error("Portfolio report generation failed:", err);
        const raw = err instanceof Error ? err.message : String(err);
        const hint =
          raw === "Failed to fetch"
            ? " (network/timeout — try fewer projects or a different time basis)"
            : "";
        setError(`Failed while ${phase}: ${raw}${hint}`);
        setStatus("error");
      }
    },
    [],
  );

  return { generate, status, error };
}
